/**
 * AuditLogTable Component - Phase 2C-3
 *
 * Table displaying audit log entries.
 * BSL Compliant: Factual data only.
 */

import { useState } from 'react';
import { AuditLogEntry } from '../../types';
import { Button, Modal } from '../common';

interface AuditLogTableProps {
  entries: AuditLogEntry[];
  loading?: boolean;
}

export function AuditLogTable({
  entries,
  loading,
}: AuditLogTableProps): JSX.Element {
  const [selectedEntry, setSelectedEntry] = useState<AuditLogEntry | null>(null);

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-slate-700 rounded mb-2" />
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-12 bg-slate-800 rounded mb-2" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <p>No audit log entries found</p>
      </div>
    );
  }

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionLabel = (action: string): string => {
    const labels: Record<string, string> = {
      pool_created: 'Pool Created',
      pool_updated: 'Pool Updated',
      pool_status_changed: 'Status Changed',
      pool_locked: 'Pool Locked',
      pool_deleted: 'Pool Deleted',
      member_joined: 'Member Joined',
      member_removed: 'Member Removed',
      balance_adjusted: 'Balance Adjusted',
      user_role_changed: 'Role Changed',
      system_config_changed: 'Config Changed',
    };
    return labels[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getActionColor = (action: string): string => {
    if (action.includes('created') || action.includes('joined')) {
      return 'text-blue-400';
    }
    if (action.includes('removed') || action.includes('deleted')) {
      return 'text-slate-400';
    }
    if (action.includes('locked') || action.includes('adjusted')) {
      return 'text-amber-400';
    }
    return 'text-slate-300';
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-700">
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Time</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Admin</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Action</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Target</th>
              <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Reason</th>
              <th className="text-right py-3 px-4 text-sm font-medium text-slate-400">Details</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((entry) => (
              <tr
                key={entry.id}
                className="border-b border-slate-800 hover:bg-slate-800/50 transition-colors"
              >
                <td className="py-3 px-4 text-sm text-slate-400">
                  {formatDate(entry.created_at)}
                </td>
                <td className="py-3 px-4">
                  <span className="font-medium text-slate-200">{entry.admin_user_id}</span>
                </td>
                <td className="py-3 px-4">
                  <span className={`font-medium ${getActionColor(entry.action)}`}>
                    {getActionLabel(entry.action)}
                  </span>
                </td>
                <td className="py-3 px-4">
                  <div className="text-sm">
                    <span className="text-slate-400">{entry.target_type}:</span>
                    <span className="ml-1 font-mono text-slate-300">{entry.target_id}</span>
                  </div>
                </td>
                <td className="py-3 px-4 text-sm text-slate-400 max-w-xs truncate">
                  {entry.reason || '—'}
                </td>
                <td className="py-3 px-4 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedEntry(entry)}
                  >
                    View
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={!!selectedEntry}
        onClose={() => setSelectedEntry(null)}
        title="Audit Entry Details"
        size="lg"
      >
        {selectedEntry && (
          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Entry ID:</span>
                <span className="ml-2 font-mono text-slate-300">{selectedEntry.id}</span>
              </div>
              <div>
                <span className="text-slate-500">Time:</span>
                <span className="ml-2 text-slate-300">
                  {new Date(selectedEntry.created_at).toLocaleString()}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Admin:</span>
                <span className="ml-2 font-medium text-slate-200">{selectedEntry.admin_user_id}</span>
              </div>
              <div>
                <span className="text-slate-500">IP Address:</span>
                <span className="ml-2 font-mono text-slate-300">{selectedEntry.ip_address || '—'}</span>
              </div>
              <div>
                <span className="text-slate-500">Action:</span>
                <span className={`ml-2 font-medium ${getActionColor(selectedEntry.action)}`}>
                  {getActionLabel(selectedEntry.action)}
                </span>
              </div>
              <div>
                <span className="text-slate-500">Target:</span>
                <span className="ml-2 text-slate-300">
                  {selectedEntry.target_type}: {selectedEntry.target_id}
                </span>
              </div>
            </div>

            {/* Reason */}
            {selectedEntry.reason && (
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Reason</h4>
                <p className="text-slate-200 p-3 bg-slate-800 rounded-lg">
                  {selectedEntry.reason}
                </p>
              </div>
            )}

            {/* Old Value */}
            {selectedEntry.old_value && (
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">Previous State</h4>
                <pre className="text-sm text-slate-300 p-3 bg-slate-800 rounded-lg overflow-x-auto">
                  {JSON.stringify(selectedEntry.old_value, null, 2)}
                </pre>
              </div>
            )}

            {/* New Value */}
            {selectedEntry.new_value && (
              <div>
                <h4 className="text-sm font-medium text-slate-400 mb-1">New State</h4>
                <pre className="text-sm text-slate-300 p-3 bg-slate-800 rounded-lg overflow-x-auto">
                  {JSON.stringify(selectedEntry.new_value, null, 2)}
                </pre>
              </div>
            )}

            {/* Close Button */}
            <div className="flex justify-end pt-4 border-t border-slate-700">
              <Button variant="secondary" onClick={() => setSelectedEntry(null)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default AuditLogTable;
